import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Labeled, TextInput } from '@tes/ui';
import { api } from '../lib/api';
import { setToken } from '../lib/auth';
import { isApiStatus, messageOf } from '../lib/errors';

/** A wrong password and a dead server are different problems; say which. */
function loginError(e: unknown): string {
  if (isApiStatus(e, 401)) return 'Invalid email or password.';
  if (isApiStatus(e, 429)) return 'Too many attempts — wait a moment and try again.';
  return messageOf(e, 'Sign-in failed. Please try again.');
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.login(email, password);
      setToken(token);
      navigate('/');
    } catch (err) {
      setError(loginError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-8 font-sans">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-4 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
      >
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
            TES eSignature
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Admin sign in</h1>
        </div>
        <Labeled label="Email">
          <TextInput
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Labeled>
        <Labeled label="Password">
          <TextInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Labeled>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
}
